import { createOrder } from '~models/Orders/createOrder';
import { ORDER_STATUS_ENUM } from '~/enums/Orders.enum';
import { INVOICE_STATUS_ENUM } from '~/enums/Invoices.enum';
import { convertToBrCurrency, formatToBrCurrency, convertToBrDate } from '~/utils/textUtils';
import { isEmptyData } from '~/utils/dataUtils';
import { adaptProduct, adaptProductTax } from '~/adapters/adaptProduct';
import type {
  IOrderList,
  IOrderItemInvoicedDTO,
  IOrderItemInvoiced,
  IOrderItemUnbilledDTO,
  IOrderItemUnbilled,
  IOrderItemsDTO,
  IOrderItems,
} from '~/models/Orders';

export function adaptOrderListFromAPI(data: unknown): IOrderList {
  return !Array.isArray(data) ? [] : data.map((order) => createOrder(order));
}

function adaptPaymentInformation({ paymentInformation }: any) {
  return isEmptyData(paymentInformation)
    ? []
    : paymentInformation.map((payment: any) => ({
        ...payment,
        amountInstallment: formatToBrCurrency(payment.amountInstallment),
        installmentDueDate: convertToBrDate(payment.installmentDueDate),
        installmentPaymentDate: convertToBrDate(payment.installmentPaymentDate),
      }));
}

function adaptInvoicesStatus({ invoiceDetails }: any) {
  return invoiceDetails.map((invoice: any) => ({
    ...invoice,
    invoiceStatus: INVOICE_STATUS_ENUM[invoice.invoiceStatus],
  }));
}

export function adaptOrderListContent(items: any) {
  return items.map((item: any) => ({
    orderNumber: item.orderNumber,
    orderDate: convertToBrDate(item.orderDate),
    finalCustomerName: item.finalCustomerName,
    orderStatus: ORDER_STATUS_ENUM[item.orderStatus],
    invoice: item.invoiceDetails.length > 0,
    bill: item.invoiceDetails.length <= 0,
  }));
}

export function adaptOrder(order: any) {
  return {
    ...order,
    invoiceDetails: adaptInvoicesStatus(order),
  };
}

export function adaptOrderStatementListContent(statements: any) {
  return isEmptyData(statements)
    ? []
    : statements.map((statement: any) => ({
        ...statement,
        paymentInformation: adaptPaymentInformation(statement),
      }));
}

export const adaptOrderItemInvoiced = {
  fromAPI: adaptOrderItemInvoicedDTO,
};

function adaptOrderItemInvoicedDTO(
  orderItemInvoicedDto: IOrderItemInvoicedDTO,
): IOrderItemInvoiced {
  return {
    ...orderItemInvoicedDto,
    totalInvoiceAmount: convertToBrCurrency(orderItemInvoicedDto.totalInvoiceAmount),
    products: orderItemInvoicedDto.products.map((product) => adaptProduct.fromAPI(product)),
    productTax: adaptProductTax.fromAPI(orderItemInvoicedDto.productTax),
  };
}

export const adaptOrderItemUnbilled = {
  fromAPI: adaptOrderItemUnbilledDTO,
};

function adaptOrderItemUnbilledDTO(
  orderItemUnbilledDto: IOrderItemUnbilledDTO,
): IOrderItemUnbilled {
  return { ...orderItemUnbilledDto };
}

export const adaptOrderItems = {
  fromAPI: adaptOrderItemsDTO,
};

function adaptOrderItemsDTO(orderItemsDto: IOrderItemsDTO): IOrderItems {
  return {
    invoicedItems: orderItemsDto.invoicedItems.map((item) => adaptOrderItemInvoicedDTO(item)),
    unbilledItems: orderItemsDto.unbilledItems.map((item) => adaptOrderItemUnbilledDTO(item)),
  };
}
